import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  FaCog,
  FaQrcode,
  FaRobot,
  FaEdit,
  FaTrashAlt,
  FaLock,
} from "react-icons/fa";
import styles from "./Settings.module.css";
import {
  CustomTextInput,
  CustomFileInput,
  CustomButton,
  CustomLoader,
} from "../../../components";
import PopupModal from "../../../components/PopupModal/PopupModal";
import AxiosInstance, {
  TokenService,
  sanitizeNameInput,
} from "../../../services/Api";
import { hasAiPlan, hasFeeModuleAddon } from "../../../utils/access";

const MAX_QR_SIZE = 2 * 1024 * 1024; // 2MB

const Settings = () => {
  const { translations } = useSelector((state) => state.language);
  const labels = translations.labels || {};

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";

  /* ── Entitlements (see src/utils/access.js) ────── */
  const feeAddonEnabled = useMemo(() => hasFeeModuleAddon(user), [user]);
  const aiPlanEnabled = useMemo(() => hasAiPlan(user), [user]);

  /* ── Bank QR state ─────────────────────────────── */
  const [qrConfig, setQrConfig] = useState(null); // saved row from API
  const [qrEditing, setQrEditing] = useState(false);
  const [bankName, setBankName] = useState("");
  const [qrFile, setQrFile] = useState(null);
  const [qrLoading, setQrLoading] = useState(true);
  const [savingQr, setSavingQr] = useState(false);
  const [qrImageBlobUrl, setQrImageBlobUrl] = useState(""); // saved QR fetched as blob

  /* ── AI config state ───────────────────────────── */
  const [aiConfig, setAiConfig] = useState(null); // saved row from API
  const [aiEditing, setAiEditing] = useState(false);
  const [aiName, setAiName] = useState("");
  const [aiKey, setAiKey] = useState("");
  const [aiDescription, setAiDescription] = useState("");
  const [aiLoading, setAiLoading] = useState(true);
  const [savingAi, setSavingAi] = useState(false);
  const [aiEditLoading, setAiEditLoading] = useState(false);

  const [errors, setErrors] = useState({});
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
  });

  /* ── Delete confirmation popup ─────────────────── */
  const [confirmState, setConfirmState] = useState({
    open: false,
    message: "",
    onConfirm: null,
  });
  const [confirmBusy, setConfirmBusy] = useState(false);

  const showModal = (type, message) =>
    setModalState({ open: true, type, message });

  const askConfirm = (message, onConfirm) =>
    setConfirmState({ open: true, message, onConfirm });

  const closeConfirm = () => {
    if (confirmBusy) return;
    setConfirmState((prev) => ({ ...prev, open: false }));
  };

  const clearError = (name) =>
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));

  /* ── Fetch existing configuration ──────────────── */
  const fetchQrConfig = useCallback(async () => {
    if (!feeAddonEnabled || !school_id) {
      setQrLoading(false);
      return;
    }
    try {
      const response = await AxiosInstance.get(
        `/settings/qr-code?school_id=${school_id}`,
      );
      setQrConfig(response.data?.data || null);
    } catch (error) {
      console.error("Failed to load Bank QR configuration:", error);
    } finally {
      setQrLoading(false);
    }
  }, [feeAddonEnabled, school_id]);

  const fetchAiConfig = useCallback(async () => {
    if (!aiPlanEnabled) {
      setAiLoading(false);
      return;
    }
    try {
      const response = await AxiosInstance.get(`/settings/ai/configuration`);
      setAiConfig(response.data?.data || null);
    } catch (error) {
      console.error("Failed to load AI configuration:", error);
    } finally {
      setAiLoading(false);
    }
  }, [aiPlanEnabled]);

  useEffect(() => {
    fetchQrConfig();
    fetchAiConfig();
  }, [fetchQrConfig, fetchAiConfig]);

  /* ── Load the saved QR image through the authenticated API as a blob
        (same logic as study material downloads) ─────────────────────── */
  useEffect(() => {
    if (!feeAddonEnabled || !qrConfig?.qr_code_image || !school_id) {
      setQrImageBlobUrl("");
      return undefined;
    }

    let objectUrl = "";
    let cancelled = false;

    AxiosInstance.get(`/settings/qr-code/image?school_id=${school_id}`, {
      responseType: "blob",
    })
      .then((response) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([response.data]));
        setQrImageBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setQrImageBlobUrl("");
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [feeAddonEnabled, qrConfig, school_id]);

  /* ── Bank QR handlers ──────────────────────────── */
  const startQrEdit = () => {
    setBankName(qrConfig?.bank_name || "");
    setQrFile(null);
    setQrEditing(true);
    setErrors((prev) => ({ ...prev, bankName: "", qrFile: "" }));
  };

  const cancelQrEdit = () => {
    setQrEditing(false);
    setQrFile(null);
    setErrors((prev) => ({ ...prev, bankName: "", qrFile: "" }));
  };

  const handleQrFileChange = (file) => {
    if (file && file.size > MAX_QR_SIZE) {
      setQrFile(null);
      setErrors((prev) => ({
        ...prev,
        qrFile: labels.qrImageSizeError || "QR image must not exceed 2MB",
      }));
      return;
    }
    setQrFile(file);
    clearError("qrFile");
  };

  const handleSaveQr = async () => {
    const nextErrors = {};
    if (!bankName.trim()) {
      nextErrors.bankName = labels.bankNameRequired || "Bank Name is required";
    }
    if (!qrConfig && !qrFile) {
      nextErrors.qrFile = labels.qrImageRequired || "QR Code image is required";
    }
    if (Object.keys(nextErrors).length) {
      setErrors((prev) => ({ ...prev, ...nextErrors }));
      return;
    }

    setSavingQr(true);
    try {
      const formData = new FormData();
      formData.append("school_id", school_id);
      formData.append("bank_name", bankName.trim());
      if (qrFile) {
        formData.append("qr_image", qrFile);
      }

      const response = qrConfig
        ? await AxiosInstance.put(`/settings/qr-code/${qrConfig.id}`, formData)
        : await AxiosInstance.post(`/settings/qr-code`, formData);

      if (response.data?.status) {
        setQrConfig(response.data.data || null);
        setQrEditing(false);
        setQrFile(null);
        showModal(
          "success",
          response.data.message ||
            labels.feeModuleQrSavedSuccess ||
            "Bank QR Code saved successfully!",
        );
      } else {
        showModal(
          "error",
          response.data?.message || "Failed to save Bank QR Code",
        );
      }
    } catch (error) {
      showModal(
        "error",
        error.response?.data?.message ||
          error.message ||
          "Failed to save Bank QR Code",
      );
    } finally {
      setSavingQr(false);
    }
  };

  const deleteQrConfig = async () => {
    if (!qrConfig) return;
    setConfirmBusy(true);
    try {
      const response = await AxiosInstance.delete(
        `/settings/qr-code/${qrConfig.id}`,
      );
      if (response.data?.status) {
        setQrConfig(null);
        setQrEditing(false);
        setBankName("");
        setQrFile(null);
        showModal(
          "success",
          response.data.message || "Bank QR Code deleted successfully",
        );
      } else {
        showModal(
          "error",
          response.data?.message || "Failed to delete Bank QR Code",
        );
      }
    } catch (error) {
      showModal(
        "error",
        error.response?.data?.message ||
          error.message ||
          "Failed to delete Bank QR Code",
      );
    } finally {
      setConfirmBusy(false);
      setConfirmState((prev) => ({ ...prev, open: false }));
    }
  };

  const handleDeleteQr = () => {
    if (!qrConfig) return;
    askConfirm(
      labels.deleteQrConfirm ||
        "The Bank QR Code will be removed from the Fee Module.",
      deleteQrConfig,
    );
  };

  /* ── AI config handlers ────────────────────────── */
  const startAiEdit = async () => {
    // Seed from the cached row so the form has values even if the refresh below
    // fails; the list response only carries api_key_masked, never the real key.
    setAiName(aiConfig?.ai_name || "");
    setAiKey("");
    setAiDescription(aiConfig?.description || "");
    setErrors((prev) => ({ ...prev, aiName: "", aiKey: "" }));

    if (!aiConfig) {
      setAiEditing(true);
      return;
    }

    setAiEditLoading(true);
    try {
      const response = await AxiosInstance.get(`/settings/ai/configuration`);
      const data = response.data?.status ? response.data.data : null;
      if (data) {
        setAiConfig(data);
        setAiName(data.ai_name || "");
        // The unmasked key, so the field shows the real value rather than
        // api_key_masked and an unchanged save round-trips it intact.
        setAiKey(data.api_key || "");
        setAiDescription(data.description || "");
      }
    } catch (error) {
      // Fall back to an empty key field, which the backend reads as
      // "keep the stored key" — the same behaviour as before this fetch.
      console.error("Failed to load AI configuration for edit:", error);
    } finally {
      setAiEditLoading(false);
      setAiEditing(true);
    }
  };

  const cancelAiEdit = () => {
    setAiEditing(false);
    setAiKey("");
    setErrors((prev) => ({ ...prev, aiName: "", aiKey: "" }));
  };

  const handleSaveAi = async () => {
    const nextErrors = {};
    if (!aiName.trim()) {
      nextErrors.aiName = labels.aiNameRequired || "AI Name is required";
    }
    if (!aiConfig && !aiKey.trim()) {
      nextErrors.aiKey = labels.aiKeyRequired || "API Key is required";
    }
    if (Object.keys(nextErrors).length) {
      setErrors((prev) => ({ ...prev, ...nextErrors }));
      return;
    }

    setSavingAi(true);
    try {
      const payload = {
        ai_name: aiName.trim(),
        api_key: aiKey.trim(), // blank on update → backend keeps stored key
        description: aiDescription.trim(),
      };

      const response = aiConfig
        ? await AxiosInstance.put(
            `/settings/ai/configuration/${aiConfig.id}`,
            payload,
          )
        : await AxiosInstance.post(`/settings/ai/configuration`, payload);

      if (response.data?.status) {
        setAiConfig(response.data.data || null);
        setAiEditing(false);
        setAiKey("");
        showModal(
          "success",
          response.data.message ||
            labels.aiModuleKeySavedSuccess ||
            "AI configuration saved successfully!",
        );
      } else {
        showModal(
          "error",
          response.data?.message || "Failed to save AI configuration",
        );
      }
    } catch (error) {
      showModal(
        "error",
        error.response?.data?.message ||
          error.message ||
          "Failed to save AI configuration",
      );
    } finally {
      setSavingAi(false);
    }
  };

  const deleteAiConfig = async () => {
    if (!aiConfig) return;
    setConfirmBusy(true);
    try {
      const response = await AxiosInstance.delete(
        `/settings/ai/configuration/${aiConfig.id}`,
      );
      if (response.data?.status) {
        setAiConfig(null);
        setAiEditing(false);
        showModal(
          "success",
          response.data.message || "AI configuration deleted successfully",
        );
      } else {
        showModal(
          "error",
          response.data?.message || "Failed to delete AI configuration",
        );
      }
    } catch (error) {
      showModal(
        "error",
        error.response?.data?.message ||
          error.message ||
          "Failed to delete AI configuration",
      );
    } finally {
      setConfirmBusy(false);
      setConfirmState((prev) => ({ ...prev, open: false }));
    }
  };

  const handleDeleteAi = () => {
    if (!aiConfig) return;
    askConfirm(
      labels.deleteAiConfirm ||
        "AI features will fall back to the default key after deletion.",
      deleteAiConfig,
    );
  };

  /* ── QR preview (new file beats saved image) ───── */
  const qrPreviewSrc = useMemo(() => {
    if (qrFile) return URL.createObjectURL(qrFile);
    return qrImageBlobUrl;
  }, [qrFile, qrImageBlobUrl]);

  useEffect(() => {
    return () => {
      if (qrPreviewSrc && qrPreviewSrc.startsWith("blob:")) {
        URL.revokeObjectURL(qrPreviewSrc);
      }
    };
  }, [qrPreviewSrc]);

  /* ── Render helpers ────────────────────────────── */
  // Shown in place of a widget when the plan / add-on is not purchased.
  const renderLocked = (message) => (
    <div className={`${styles.emptyState} ${styles.lockedState}`}>
      <FaLock />
      <p>{message}</p>
    </div>
  );

  const renderChip = (configured) => (
    <span
      className={`${styles.chip} ${
        configured ? styles.chipConfigured : styles.chipNotConfigured
      }`}
    >
      {configured
        ? labels.configured || "Configured"
        : labels.notConfigured || "Not Configured"}
    </span>
  );

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? labels.success || "Success"
            : labels.failed || "Failed"
        }
        message={modalState.message}
        onOk={() => setModalState((prev) => ({ ...prev, open: false }))}
      />

      {/* Delete confirmation (replaces the browser/Swal confirm) */}
      <PopupModal
        open={confirmState.open}
        title={labels.areYouSure || "Are you sure?"}
        onClose={closeConfirm}
        render
        renderData={
          <div className={styles.confirmBody}>
            <p className={styles.confirmMessage}>{confirmState.message}</p>
            <div className={styles.actionsRow}>
              <CustomButton
                type="button"
                variant="outlined"
                fullWidth={false}
                onClick={closeConfirm}
                disabled={confirmBusy}
              >
                {labels.cancel || "Cancel"}
              </CustomButton>
              <CustomButton
                type="button"
                variant="contained"
                color="error"
                fullWidth={false}
                icon={<FaTrashAlt />}
                loading={confirmBusy}
                loadingText={labels.deleting || "Deleting..."}
                onClick={() => confirmState.onConfirm?.()}
              >
                {labels.delete || "Delete"}
              </CustomButton>
            </div>
          </div>
        }
      />

      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCog />
          </div>
          <h1 className={styles.headerTitle}>{labels.settings || "Settings"}</h1>
        </div>

        <div className={styles.cardsGrid}>
          {/* ═══════════ Bank QR — Fee Module ═══════════ */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderIcon}>
                <FaQrcode />
              </div>
              <h2 className={styles.cardTitle}>
                {labels.feeModuleQr || "Fee Module – Bank QR Code"}
              </h2>
              {feeAddonEnabled ? renderChip(Boolean(qrConfig)) : null}
            </div>

            <div className={styles.cardBody}>
              {!feeAddonEnabled ? (
                renderLocked(
                  labels.feeModuleAddonRequired ||
                    "This feature is accessible only when you purchase the Fee Module add-on.",
                )
              ) : qrLoading ? (
                <CustomLoader />
              ) : !qrEditing && qrConfig ? (
                <>
                  <div className={styles.qrPreviewWrap}>
                    <div className={styles.qrPreviewBox}>
                      <img src={qrPreviewSrc} alt="Bank QR code" />
                      <div className={styles.qrPreviewCaption}>
                        {qrConfig.bank_name}
                      </div>
                    </div>
                  </div>
                  <div className={styles.viewRow}>
                    <span className={styles.viewLabel}>
                      {labels.bankName || "Bank Name"}
                    </span>
                    <span className={styles.viewValue}>
                      {qrConfig.bank_name}
                    </span>
                  </div>
                  <div className={styles.actionsRow}>
                    <CustomButton
                      type="button"
                      variant="outlined"
                      fullWidth={false}
                      icon={<FaEdit />}
                      onClick={startQrEdit}
                    >
                      {labels.edit || "Edit"}
                    </CustomButton>
                    <CustomButton
                      type="button"
                      variant="outlined"
                      color="error"
                      fullWidth={false}
                      icon={<FaTrashAlt />}
                      onClick={handleDeleteQr}
                    >
                      {labels.delete || "Delete"}
                    </CustomButton>
                  </div>
                </>
              ) : !qrEditing && !qrConfig ? (
                <>
                  <div className={styles.emptyState}>
                    <FaQrcode />
                    <p>
                      {labels.noQrConfigured ||
                        "No Bank QR Code configured yet. Add one so parents can scan and pay fees."}
                    </p>
                  </div>
                  <div className={styles.actionsRow}>
                    <CustomButton
                      type="button"
                      variant="contained"
                      fullWidth={false}
                      onClick={startQrEdit}
                    >
                      {labels.addQrCode || "Add QR Code"}
                    </CustomButton>
                  </div>
                </>
              ) : (
                <>
                  <CustomTextInput
                    label={labels.bankName || "Bank Name"}
                    name="bankName"
                    type="text"
                    value={bankName}
                    onChange={(e) => {
                      setBankName(sanitizeNameInput(e.target.value));
                      clearError("bankName");
                    }}
                    required
                    error={errors.bankName}
                  />

                  <CustomFileInput
                    label={labels.qrCodeImage || "QR Code Image (PNG, JPEG)"}
                    name="qrFile"
                    value={qrFile}
                    onChange={handleQrFileChange}
                    accept="image/png,image/jpeg,image/jpg"
                    buttonText={labels.chooseFile || "Choose File"}
                    required={!qrConfig}
                    error={errors.qrFile}
                  />

                  {qrPreviewSrc ? (
                    <div className={styles.qrPreviewWrap}>
                      <div className={styles.qrPreviewBox}>
                        <img src={qrPreviewSrc} alt="QR preview" />
                        <div className={styles.qrPreviewCaption}>
                          {qrFile
                            ? labels.newQrPreview || "New QR preview"
                            : labels.currentQr || "Current QR"}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <p className={styles.helperNote}>
                    {labels.qrHelperNote ||
                      "Supported formats: PNG, JPG, JPEG. Max size 2MB."}
                  </p>

                  <div className={styles.actionsRow}>
                    <CustomButton
                      type="button"
                      variant="outlined"
                      fullWidth={false}
                      onClick={cancelQrEdit}
                      disabled={savingQr}
                    >
                      {labels.cancel || "Cancel"}
                    </CustomButton>
                    <CustomButton
                      type="button"
                      variant="contained"
                      fullWidth={false}
                      loading={savingQr}
                      loadingText={labels.saving || "Saving..."}
                      onClick={handleSaveQr}
                    >
                      {qrConfig
                        ? labels.updateButton || "Update"
                        : labels.saveButton || "Save"}
                    </CustomButton>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ═══════════ AI Key Configuration ═══════════ */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderIcon}>
                <FaRobot />
              </div>
              <h2 className={styles.cardTitle}>
                {labels.aiKeyConfiguration || "AI Key Configuration"}
              </h2>
              {aiPlanEnabled ? renderChip(Boolean(aiConfig)) : null}
            </div>

            <div className={styles.cardBody}>
              {!aiPlanEnabled ? (
                renderLocked(
                  labels.aiPlanRequired ||
                    "This feature is accessible only when you purchase the AI Plan.",
                )
              ) : aiLoading ? (
                <CustomLoader />
              ) : !aiEditing && aiConfig ? (
                <>
                  <div className={styles.viewRow}>
                    <span className={styles.viewLabel}>
                      {labels.aiName || "AI Name"}
                    </span>
                    <span className={styles.viewValue}>{aiConfig.ai_name}</span>
                  </div>
                  <div className={styles.viewRow}>
                    <span className={styles.viewLabel}>
                      {labels.apiKey || "API Key"}
                    </span>
                    <span className={styles.viewValue}>
                      {aiConfig.api_key_masked}
                    </span>
                  </div>
                  <div className={styles.viewRow}>
                    <span className={styles.viewLabel}>
                      {labels.description || "Description"}
                    </span>
                    <span className={styles.viewValue}>
                      {aiConfig.description || "—"}
                    </span>
                  </div>
                  <div className={styles.actionsRow}>
                    <CustomButton
                      type="button"
                      variant="outlined"
                      fullWidth={false}
                      icon={<FaEdit />}
                      onClick={startAiEdit}
                      loading={aiEditLoading}
                      loadingText={labels.loading || "Loading..."}
                    >
                      {labels.edit || "Edit"}
                    </CustomButton>
                    <CustomButton
                      type="button"
                      variant="outlined"
                      color="error"
                      fullWidth={false}
                      icon={<FaTrashAlt />}
                      onClick={handleDeleteAi}
                      disabled={aiEditLoading}
                    >
                      {labels.delete || "Delete"}
                    </CustomButton>
                  </div>
                </>
              ) : !aiEditing && !aiConfig ? (
                <>
                  <div className={styles.emptyState}>
                    <FaRobot />
                    <p>
                      {labels.noAiConfigured ||
                        "No AI key configured yet. AI features (Add Book, Lesson Plan, Assessment, Remedial, Study Material) will use the default key."}
                    </p>
                  </div>
                  <div className={styles.actionsRow}>
                    <CustomButton
                      type="button"
                      variant="contained"
                      fullWidth={false}
                      onClick={startAiEdit}
                    >
                      {labels.addAiKey || "Add AI Key"}
                    </CustomButton>
                  </div>
                </>
              ) : (
                <>
                  <CustomTextInput
                    label={labels.aiName || "AI Name"}
                    name="aiName"
                    type="text"
                    placeholder="Gemini"
                    value={aiName}
                    onChange={(e) => {
                      setAiName(sanitizeNameInput(e.target.value));
                      clearError("aiName");
                    }}
                    required
                    error={errors.aiName}
                  />

                  <CustomTextInput
                    label={labels.apiKey || "API Key"}
                    name="aiKey"
                    type="password"
                    placeholder={
                      aiConfig
                        ? aiConfig.api_key_masked ||
                          "Leave blank to keep the current key"
                        : "AIzaSyXXXXXXXXXXXX"
                    }
                    value={aiKey}
                    onChange={(e) => {
                      setAiKey(e.target.value);
                      clearError("aiKey");
                    }}
                    required={!aiConfig}
                    error={errors.aiKey}
                  />

                  {/* Only meaningful while the field is empty — once the saved
                      key has been loaded into it, there is nothing to keep. */}
                  {aiConfig && !aiKey ? (
                    <p className={styles.helperNote}>
                      {labels.aiKeyKeepNote ||
                        "Leave the API Key blank to keep the currently saved key."}
                    </p>
                  ) : null}

                  <CustomTextInput
                    label={labels.description || "Description"}
                    name="aiDescription"
                    multiline
                    rows={3}
                    placeholder={
                      labels.aiDescriptionPlaceholder ||
                      "Gemini AI API key configuration for AI-powered educational features."
                    }
                    value={aiDescription}
                    onChange={(e) => setAiDescription(e.target.value)}
                  />

                  <div className={styles.actionsRow}>
                    <CustomButton
                      type="button"
                      variant="outlined"
                      fullWidth={false}
                      onClick={cancelAiEdit}
                      disabled={savingAi}
                    >
                      {labels.cancel || "Cancel"}
                    </CustomButton>
                    <CustomButton
                      type="button"
                      variant="contained"
                      fullWidth={false}
                      loading={savingAi}
                      loadingText={labels.saving || "Saving..."}
                      onClick={handleSaveAi}
                    >
                      {aiConfig
                        ? labels.updateButton || "Update"
                        : labels.saveButton || "Save"}
                    </CustomButton>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Settings;
